import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { CheckCircle2, Minus, Plus, Ticket, ArrowRight, X } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import { buildTokens, tokensToCssVars } from './storefrontTheme';
import './storefront.css';

const MealPassPurchaseModal = ({ program, open, onClose, onComplete, darkMode = false, brandingHint = null, storeSlug = null, mealItems = [], brand = null }) => {
  const { addMealPassToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState(null);
  const [error, setError] = useState(null);

  // Reset state when program changes / modal closes
  useEffect(() => {
    if (!open) return;
    setQuantity(1);
    setPendingPurchase(null);
    setError(null);
  }, [open, program?.id]);

  const total = (program?.price || 0) * quantity;
  const perCredit = program?.credit_count ? (program.price / program.credit_count).toFixed(2) : null;
  // v5.66 — the dialog portals OUTSIDE the page shell, so it derives the
  // same tokens itself and sets the variables on its own content node.
  const tokens = buildTokens(brand || brandingHint || {});
  const primary = tokens.primary;
  const accent = tokens.accent;
  const programPhoto = program?.image_url || program?.hero_image_url || null;

  // What the pass can be redeemed for. selected_items → the restaurant's
  // configured list; otherwise (all_items, or a legacy program with the mode
  // unset) → the whole live menu is redeemable.
  const redeemables = (() => {
    const mode = program?.eligible_item_mode;
    if (mode === 'selected_items' && Array.isArray(program?.eligible_items) && program.eligible_items.length) {
      return program.eligible_items.map((it, i) => ({
        key: it.item_id || `e${i}`,
        name: it.item_name,
        fee: Number(it.premium_upgrade_fee) > 0 ? Number(it.premium_upgrade_fee) : 0,
      }));
    }
    return (mealItems || [])
      .filter((mi) => mi && mi.name && mi.is_active !== false)
      .map((mi, i) => ({ key: mi.id || `m${i}`, name: mi.name, fee: 0 }));
  })();

  // v4.3.7 — Meal passes now flow through the SAME cart + Stripe checkout as
  // meal plans. No separate "pending payment" step. Adding to cart triggers
  // the parent to show the AddonsOfferModal (if any) or the cart pill; when
  // the customer proceeds to checkout, Stripe charges once and the backend
  // grants credits for any meal-pass line items after payment succeeds.
  const handleConfirm = () => {
    try {
      setPurchasing(true);
      setError(null);
      addMealPassToCart(program, {
        quantity,
        storeSlug: storeSlug || undefined,
        storeId: program.store_id || program.valid_store_ids?.[0] || null,
        storeName: program.store_name || undefined,
      });
      // Show a brief "added to cart" confirmation, then close so the storefront
      // can decide what to do next (upsell add-ons, or nudge to checkout).
      setPendingPurchase({ status: 'added_to_cart', quantity });
      // Auto-close after a moment so the merchant's cart pill / add-ons upsell can appear
      setTimeout(() => {
        onComplete?.();
        onClose();
      }, 900);
    } catch (err) {
      console.error('Add to cart error:', err);
      setError('Could not add this pass to your cart');
    } finally {
      setPurchasing(false);
    }
  };

  const handleClose = () => {
    if (pendingPurchase) onComplete?.();
    onClose();
  };

  const inc = () => setQuantity((q) => Math.min(10, q + 1));
  const dec = () => setQuantity((q) => Math.max(1, q - 1));

  if (!program) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="p-0 overflow-hidden max-w-md border-0 shadow-2xl sf-root"
        data-testid="mealpass-purchase-modal"
        style={{ ...tokensToCssVars(tokens), borderRadius: 'calc(var(--sf-radius) * 1.2)' }}
      >
        {/* Hidden but present for accessibility (Radix requirement) */}
        <DialogTitle className="sr-only">
          {pendingPurchase ? 'Purchase confirmation' : `Purchase ${program.public_name || program.name}`}
        </DialogTitle>

        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          data-testid="mealpass-purchase-close"
          className="sf-icon-btn absolute top-3 right-3 z-10 !w-9 !h-9"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <AnimatePresence mode="wait">
          {pendingPurchase ? (
            /* ─── Confirmation state ─────────────────────────────────── */
            <motion.div
              key="confirmed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="p-10 text-center"
              data-testid="mealpass-purchase-pending-state"
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 180 }}
                className="mx-auto w-16 h-16 rounded-full grid place-items-center mb-5"
                style={{ background: `${primary}20`, color: primary }}
              >
                <CheckCircle2 className="w-8 h-8" strokeWidth={2} />
              </motion.div>
              <h3
                className="mb-3 leading-tight"
                style={{
                  fontFamily: 'var(--sf-heading)',
                  fontWeight: 500,
                  fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                }}
              >
                Added to{' '}
                <span style={{ fontFamily: 'var(--sf-script)', color: primary, fontSize: '1.2em' }}>
                  cart.
                </span>
              </h3>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6 max-w-sm mx-auto">
                {pendingPurchase?.quantity && Number(pendingPurchase.quantity) > 1
                  ? `${pendingPurchase.quantity} passes added.`
                  : 'Pass added.'}{' '}
                Head to checkout when you're ready, or keep shopping.
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-medium border-2 transition-colors hover:text-white"
                style={{ borderColor: primary, color: primary, background: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = primary; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = primary; }}
              >
                Close
              </button>
            </motion.div>
          ) : (
            /* ─── Purchase form state ────────────────────────────────── */
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              /* Cap to the viewport and scroll — a long "what you can redeem"
                 list must never push the price + Add-to-cart CTA off-screen. */
              className="max-h-[88vh] overflow-y-auto"
            >
              {/* Header: the program's photo when it has one, else a brand stripe */}
              {programPhoto ? (
                <div className="relative h-44 sm:h-52 overflow-hidden" data-testid="mealpass-purchase-photo">
                  <img src={programPhoto} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                </div>
              ) : (
                <div className="relative h-24 overflow-hidden" style={{ background: primary }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Ticket className="w-12 h-12 opacity-40" strokeWidth={1.2} style={{ color: 'var(--sf-on-primary)' }} />
                  </div>
                </div>
              )}

              <div className="px-8 pt-6 pb-8">
                <span className="sf-eyebrow !mb-2">
                  Meal pass
                </span>
                <h3
                  className="leading-tight mb-1"
                  style={{
                    fontFamily: 'var(--sf-heading)',
                    fontWeight: 500,
                    fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                  }}
                >
                  {program.public_name || program.name}
                </h3>
                {program.savings_label && (
                  <p style={{ fontFamily: 'var(--sf-script)', color: primary, fontSize: '1.35rem', lineHeight: 1 }}>
                    {program.savings_label}
                  </p>
                )}

                {program.description && (
                  <p className="text-sm text-neutral-600 leading-relaxed mt-4">
                    {program.description}
                  </p>
                )}

                {/* What you can redeem — the restaurant's allowed items (or the
                    whole menu for an all-items pass), so shoppers know what a
                    pass buys before purchasing. */}
                <div className="mt-5" data-testid="mealpass-redeemable-items">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2">What you can redeem</div>
                  {redeemables.length > 0 ? (
                    <ul className="text-sm text-neutral-700 space-y-1 max-h-40 overflow-y-auto pr-1">
                      {redeemables.map((it) => (
                        <li key={it.key} className="flex justify-between gap-3">
                          <span className="truncate">{it.name}</span>
                          {it.fee > 0 && (
                            <span className="text-neutral-400 text-xs flex-shrink-0">{`+$${it.fee.toFixed(2)}`}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-neutral-700">Redeem for any item on the menu.</p>
                  )}
                </div>

                {/* Quantity stepper */}
                <div className="mt-6 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">Quantity</div>
                    <div className="text-neutral-600 text-xs">Buy multiple for family or friends.</div>
                  </div>
                  <div className="flex items-center gap-3 rounded-full border border-neutral-300 bg-white p-1">
                    <button
                      type="button"
                      onClick={dec}
                      disabled={quantity <= 1}
                      className="w-8 h-8 rounded-full grid place-items-center hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid="mealpass-purchase-qty-dec"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span
                      className="min-w-[24px] text-center text-lg font-medium"
                      style={{ fontFamily: 'var(--sf-heading)' }}
                      data-testid="mealpass-purchase-quantity-input"
                    >
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={inc}
                      disabled={quantity >= 10}
                      className="w-8 h-8 rounded-full grid place-items-center hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid="mealpass-purchase-qty-inc"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Order summary */}
                <div className="mt-6 sf-card p-5">
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-sm text-neutral-600">Credits included</span>
                    <span
                      className="text-2xl font-medium"
                      style={{ fontFamily: 'var(--sf-heading)' }}
                    >
                      {program.credit_count * quantity}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between mb-3 text-sm">
                    <span className="text-neutral-600">Price per pass</span>
                    <span>{program.currency || 'CAD'} ${program.price?.toFixed(2)}</span>
                  </div>
                  {perCredit && (
                    <div className="flex items-baseline justify-between mb-3 text-sm">
                      <span className="text-neutral-600">Per meal</span>
                      <span className="text-neutral-500">{program.currency || 'CAD'} ${perCredit}</span>
                    </div>
                  )}
                  <div className="border-t border-neutral-200 mt-3 pt-3 flex items-baseline justify-between">
                    <span
                      style={{
                        fontFamily: 'var(--sf-heading)',
                        fontStyle: 'italic',
                        fontSize: '1rem',
                      }}
                    >
                      Total today
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--sf-heading)',
                        fontWeight: 600,
                        fontSize: '1.75rem',
                        color: primary,
                        lineHeight: 1,
                      }}
                      data-testid="mealpass-purchase-total"
                    >
                      {program.currency || 'CAD'} ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                {error && (
                  <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm border border-red-200">
                    {error}
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={purchasing}
                    className="flex-1 sf-btn sf-btn-ghost"
                  >
                    Cancel
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={handleConfirm}
                    disabled={purchasing}
                    data-testid="mealpass-purchase-confirm-button"
                    className="flex-1 sf-btn sf-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {purchasing ? 'Adding…' : 'Add to cart'}
                    {!purchasing && <Plus className="w-4 h-4" />}
                  </motion.button>
                </div>

                <p className="mt-4 text-[10px] tracking-widest uppercase text-center text-neutral-400">
                  Credits never expire · Restaurant-honored
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default MealPassPurchaseModal;
